import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const contentTR = `
**Mystic Tarot Gizlilik Politikası / Privacy Policy**
**Son Güncelleme / Last Updated: 23.11.2025**

## 1. Giriş
Mystic Tarot ("Uygulama", "biz", "bize", "bizim") kullanıcılarının gizliliğini korumayı taahhüt eder. Bu gizlilik politikası, uygulamayı ve web sitemizi kullanırken hangi verileri topladığımızı, nasıl kullandığımızı ve haklarınızı açıklar.

## 2. Topladığımız Veriler
Uygulama içinde aşağıdaki veri türleri toplanabilir:

**Hesap Bilgileri:** e-posta adresi, kullanıcı adı (kayıt ve giriş için).

**Kullanıcı İçerikleri:**
- İlişki analizi için yazılan metinler, mesaj örnekleri, sorular.
- Tarot falı için girilen sorular/niyetler.
- Kahve falı için yüklenen fotoğraflar (fincan görselleri).

**Cihaz ve Kullanım Verileri:** cihaz türü, işletim sistemi, tarayıcı bilgisi, uygulama içi etkileşimler, hata kayıtları (uygulamayı iyileştirmek için).

**Ödeme Bilgileri:** Uygulama içinde ödeme altyapısı olarak Google Play Billing kullanılır. Kart bilgileriniz tarafımızca tutulmaz; ödeme işlemi Google Play üzerinden güvenli şekilde gerçekleşir.

**Toplamadığımız Veriler:**
Konum verisi, sağlık verisi, rehber/kişiler, SMS/arama kayıtları, biyometrik veriler tarafımızca toplanmaz.

## 3. Verileri Ne Amaçla Kullanırız?
Toplanan veriler şu amaçlarla kullanılır:
- Uygulama hizmetlerini sunmak (analiz üretmek, fal yorumlamak, sonuç göstermek)
- Hesap oluşturma ve giriş işlemleri
- Premium abonelik ve ödeme işlemlerini yürütmek
- Müşteri desteği sağlamak
- Uygulama güvenliği ve hata tespiti / performans iyileştirmesi
- Yasal yükümlülükleri yerine getirmek

## 4. İçerik ve Sorumluluk Reddi
Mystic Tarot, eğlence ve rehberlik amaçlı içerikler üretir.
Üretilen analizler kesin doğruluk iddiası taşımaz; sağlık/hukuk/psikoloji gibi profesyonel alanlarda teşhis veya kesin karar yerine geçmez.

## 5. Verilerin Paylaşımı
Verilerinizi şu durumlar dışında üçüncü kişilerle paylaşmayız:
- **Ödeme sağlayıcıları:** Google Play Billing (abonelik/ödeme için)
- **Barındırma ve altyapı sağlayıcıları:** uygulamayı çalıştırmak ve güvenliği sağlamak için
- **Yasal zorunluluk:** resmi makamların talebi halinde

## 6. Verilerin Saklanma Süresi
Veriler, hizmetin sağlanması için gerekli süre boyunca saklanır.
Hesabınızı sildiğinizde, yasal zorunluluklar hariç verileriniz silinir veya anonimleştirilir.

## 7. Güvenlik
Verilerinizin korunması için teknik ve idari güvenlik önlemleri uygularız. Her şeye rağmen internet üzerinden yapılan veri iletiminin %100 güvenli olduğu garanti edilemez.

## 8. Haklarınız (KVKK/GDPR)
Türkiye KVKK ve ilgili mevzuat kapsamında:
- Verilerinize erişme
- Düzeltme isteme
- Silinmesini / anonimleştirilmesini isteme
- İtiraz etme
- Onayı geri çekme
haklarına sahipsiniz.

## 9. Çocukların Gizliliği
Uygulama 18 yaş ve üzeri kullanıcılar içindir ve çocuklara yönelik değildir. 18 yaş altı kullanıcıların verileri bilerek toplanmaz.

## 10. İletişim
Gizlilik ile ilgili sorularınız için bize ulaşın:
- **E-posta:** support@mysticai.com.tr
- **Web:** https://mysticai.com.tr
`;

  const contentEN = `
**Mystic Tarot Privacy Policy**
**Last Updated: 23.11.2025**

## 1. Introduction
Mystic Tarot ("App", "we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains what data we collect, how we use it, and your rights when using our app and website.

## 2. Data We Collect
We may collect the following data:

**Account Information:** email address, username (for sign-up/login).

**User Content:**
- Text you provide for relationship analysis, message samples, questions.
- Tarot questions/intent text.
- Coffee fortune photos you upload (cup images).

**Device & Usage Data:** device type, OS, browser info, in-app interactions, crash/error logs (to improve performance).

**Payment Data:** We use Google Play Billing for payments. We do not store your card details; payments are processed securely by Google Play.

**Data We Do Not Collect:**
We do not collect location data, health data, contacts, SMS/call logs, or biometric data.

## 3. How We Use Data
We use data to:
- Provide app services (generate personalized readings)
- Create and manage accounts
- Process premium subscriptions and payments
- Provide customer support
- Maintain security and improve performance
- Comply with legal obligations

## 4. Content Disclaimer
Mystic Tarot provides content for entertainment and guidance.
Results are not guaranteed to be accurate and are not a substitute for professional medical/legal/psychological advice.

## 5. Data Sharing
We do not share your data except:
- **Payment providers:** Google Play Billing (for subscriptions/payments)
- **Hosting/infrastructure providers:** to operate and secure the app
- **Legal requirements:** when required by authorities

## 6. Data Retention
We retain data as long as necessary to provide the service.
If you delete your account, your data will be deleted or anonymized unless legally required to keep it.

## 7. Security
We apply technical and organizational measures to protect your data. However, no online transmission is 100% secure.

## 8. Your Rights
Under applicable data protection laws (KVKK/GDPR), you may:
- Access your data
- Request correction
- Request deletion/anonymization
- Object to processing
- Withdraw consent

## 9. Children's Privacy
The app is intended for users 18+ and not directed to children. We do not knowingly collect data from minors.

## 10. Contact
For any privacy questions:
- **Email:** support@mysticai.com.tr
- **Website:** https://mysticai.com.tr
`;

  const content = i18n.language === 'tr' ? contentTR : contentEN;

  const renderContent = (text) => {
    return text.split('\n').map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index} className="privacy-h2">{line.replace('## ', '')}</h2>;
      } else if (line.startsWith('**') && line.endsWith('**')) {
        return <h3 key={index} className="privacy-h3">{line.replace(/\*\*/g, '')}</h3>;
      } else if (line.startsWith('- ')) {
        return <li key={index} className="privacy-li">{line.replace('- ', '')}</li>;
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        return <p key={index} className="privacy-p">{line}</p>;
      }
    });
  };

  return (
    <div className="privacy-container">
      <nav className="privacy-nav">
        <button onClick={() => navigate('/')} className="privacy-back-btn">
          ← {t('common.back')}
        </button>
        <div className="logo">
          <img src="/logo.png" alt="Mystic Tarot" style={{ height: '60px', width: 'auto' }} />
        </div>
      </nav>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="privacy-content"
      >
        <h1 className="privacy-h1">{t('privacy.title')}</h1>
        <p className="privacy-date">{t('privacy.lastUpdated')}</p>
        <div className="privacy-text">
          {renderContent(content)}
        </div>
      </motion.div>

      <footer className="privacy-footer">
        <p>© 2025 Mystic Tarot. {t('footer.rights')}</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
